import { Policy, RuleModel, RulesEngineRunLog } from '../../../../types';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRuleRunLogs, RulesKeys } from '../../../api/rules-engine-api';
import { useCallback, useMemo, useState } from 'react';
import RulesEngineActionLogsDialog from './rules-engine-action-logs-dialog';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import StatusLabel, { StatusType } from '../../../shared/status-label';

import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import History from '@mui/icons-material/History';
import Refresh from '@mui/icons-material/Refresh';

interface RulesEngineRunLogsGridProps {
  ruleId: number;
  policies: Policy[];
  rules: RuleModel[];
  height?: number;
}

export default function RulesEngineRunLogsGrid({
  ruleId,
  policies,
  rules,
  height = 400,
}: RulesEngineRunLogsGridProps) {
  const queryClient = useQueryClient();
  const [selectedRow, setSelectedRow] = useState<RulesEngineRunLog | null>(
    null
  );

  const { isLoading, data, isFetching } = useQuery({
    queryKey: [RulesKeys.RunLogs, ruleId],
    queryFn: () => getRuleRunLogs(ruleId),
    enabled: !!ruleId,
    meta: {
      errorMessage:
        'There was an issue loading logs data. Please refresh and try again.',
    },
  });

  const CustomToolbar = useCallback(
    function CustomToolbar() {
      return (
        <SharedToolbar noColumn>
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [RulesKeys.RunLogs, ruleId],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </SharedToolbar>
      );
    },
    [queryClient, ruleId]
  );

  const columns: GridColDef<RulesEngineRunLog>[] = useMemo(
    () => [
      {
        field: 'startTime',
        headerName: 'Start Time',
        flex: 1,
        type: 'dateTime',
        valueGetter: (value: number | undefined) =>
          value ? new Date(value) : null,
        valueFormatter: (value: Date | null) =>
          value ? value.toLocaleString() : 'N/A',
      },
      {
        field: 'endTime',
        headerName: 'End Time',
        flex: 1,
        type: 'dateTime',
        valueGetter: (value: number | undefined) =>
          value ? new Date(value) : null,
        valueFormatter: (value: Date | null) =>
          value ? value.toLocaleString() : 'In Progress',
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        renderCell: (params) => {
          const status = params.row.status
            ? (params.row.status?.toUpperCase() as StatusType)
            : 'UNKNOWN';
          return status !== 'UNKNOWN' ? <StatusLabel status={status} /> : null;
        },
      },
    ],
    []
  );

  return (
    <>
      <SharedDataGridClient
        rows={data || []}
        columns={columns}
        loading={isLoading || isFetching}
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Logs', <History />),
        }}
        sx={{
          mt: 1,
          height: height,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        onRowClick={(params) => {
          setSelectedRow(params.row as RulesEngineRunLog);
        }}
        defaultGridState={{
          sorting: {
            sortModel: [{ field: 'startTime', sort: 'desc' }],
          },
        }}
        density="compact"
        disableRowSelectionOnClick
      />
      {selectedRow && (
        <RulesEngineActionLogsDialog
          open={Boolean(selectedRow)}
          handleClose={() => setSelectedRow(null)}
          runLogId={selectedRow.id}
          policies={policies}
          rules={rules}
        />
      )}
    </>
  );
}
